import './assets/main.css'

import 'reflect-metadata' // for typeserializer
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import i18n, { initPrimeVueLocaleSync } from '@/plugins/i18n'
import { getFirebaseApp } from '@/plugins/firestore'
import { preloadPrimeVueComponentThemes } from '@/plugins/primevueThemePreload'
import { makePrimeVueDialogsStatic } from '@/plugins/primevueDialogDefaults'
import { VueFire, VueFireAuth } from 'vuefire'

import App from './App.vue'
import router from './router'
import PrimeVue from 'primevue/config'
import Aura from '@primeuix/themes/aura'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import { definePreset } from '@primeuix/themes'
import { useAppStore } from '@/stores/app'

import { palette } from '@primeuix/themes';
import { BRAND_COLORS } from '@/theme/brand'
const palettePrimaryColors = palette(BRAND_COLORS.primary);
const paletteInfoColors = palette(BRAND_COLORS.info);
const paletteWarnColors = palette(BRAND_COLORS.warn);

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: palettePrimaryColors,
    info: paletteInfoColors,
    warn: paletteWarnColors,
  },
  components: {
    tooltip: {
      colorScheme: {
        light: {
          root: {
            padding: '0.25rem 0.375rem',
          },
        },
      },
      extend: {
        fontSize: '0.725rem',
        lineHeight: '1.2',
      },
      css: ({ dt }) => `
        .p-tooltip .p-tooltip-text {
          font-size: ${dt('tooltip.fontSize')};
          text-align: center;
          line-height: ${dt('tooltip.lineHeight')};
        }
      `,
    },
    toast: {
      // PrimeVue's severity icon sits high against the summary it labels: at
      // `align-items: flex-start` every child's BOX top meets the summary's, so
      // a child shorter than the line sits above its centre. The 1.125rem icon
      // against a 1rem/1.6 line falls 3.8px short — hence 4px, measured on the
      // running app rather than derived here.
      //
      // `.p-icon` is the hook, not `.p-toast-message-icon`: the by-name version
      // did not move the icon in the browser. Both classes should land on the
      // same <svg> (`cx('messageIcon')` on the component, `pti()` adding
      // `p-icon` inside `BaseIcon`, `inheritAttrs` merging them), so this is a
      // reading of the running app over a reading of the source, and the app
      // wins.
      //
      // The hook is not the scope, and `>` and `-content` are both load-bearing.
      // `p-icon` is on EVERY PrimeVue SVG in the app — dropdown chevrons,
      // checkbox ticks, paginator arrows, the password reveal — so unqualified
      // it nudges all of them to fix one toast. The close button's own SVG
      // carries it too, one level deeper, so a plain descendant selector would
      // tilt the glyph inside the button. The severity icon is a DIRECT child of
      // `.p-toast-message-content`; the close icon is not.
      //
      // The close button is deliberately left alone. PrimeVue positions it with
      // a percentage vertical margin (`margin: -25% 0 0 0; right: -25%`), which
      // resolves against the container's WIDTH — but it renders acceptably in
      // the app, and it is not this card's business to redesign the toast.
      css: () => `
        .p-toast-message-content > .p-icon {
          margin-top: 4px;
        }
      `,
    },
  },
})

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(VueFire, {
  firebaseApp: await getFirebaseApp,
  modules: [VueFireAuth()],
})

// Load public config before router starts (no authentication required)
const appStore = useAppStore()
await appStore.loadPublicConfig()

app.use(router)
app.use(i18n)
app.use(PrimeVue, {
  theme: {
    preset: MyPreset,
    options: {
      darkModeSelector: false || 'none',
    },
  },
})
// Right after the theme is installed and before anything mounts: the popover
// theme is injected here rather than left to PrimeVue's first-instance lazy
// load, which this app's Overview screen defeats (see the module header).
preloadPrimeVueComponentThemes()
// Before anything mounts: Vue reads a prop's default on the component's first
// use (see the module header).
makePrimeVueDialogsStatic()
initPrimeVueLocaleSync(app)
app.use(ToastService)
app.use(ConfirmationService)

app.mount('#app')
